import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { 
  Users, 
  User, 
  MonitorPlay, 
  MapPin, 
  Clock, 
  Calendar,
  Zap,
  Target,
  ArrowRight
} from 'lucide-react';

const Courses = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const courseTypes = [
    {
      icon: Users,
      title: t('groupClasses'),
      subtitle: t('subtitlegroup'),
      description: t('descriptiongroup'),
      image: 'https://images.unsplash.com/photo-1574130303188-31a915382726',
      features: t('featuresgroup'),
      duration: t('durationgroup'),
      schedule: t('schedulegroup'),
      color: 'from-blue-500 to-blue-700',
      badge: t('badgegroup'),
    },
    {
      icon: User,
      title: t('privateLesson'),
      subtitle: t('subtitleprivate'),
      description: t('descriptionprivate'),
      image: 'https://images.pexels.com/photos/8728240/pexels-photo-8728240.jpeg',
      features: t('featuresprivate'),
      duration: t('durationprivate'),
      schedule: t('scheduleprivate'),
      color: 'from-purple-500 to-purple-700',
      badge: t('badgeprivate'),
    },
    {
      icon: MonitorPlay,
      title: t('onlineClasses'),
      subtitle: t('subtitleonline'),
      description: t('descriptiononline'),
      image: 'https://images.unsplash.com/photo-1541178735493-479c1a27ed24',
      features: t('featuresonline'),
      duration: t('durationonline'),
      schedule: t('scheduleonline'),
      color: 'from-green-500 to-green-700',
      badge: t('badgeonline'),
    },
    {
      icon: Zap,
      title: t('intensiveCourses'),
      subtitle: t('subtitleintensive'),
      description: t('descriptionintensive'),
      image: 'https://images.unsplash.com/photo-1555111359-851254288029',
      features: t('featuresintensive'),
      duration: t('durationintensive'),
      schedule: t('scheduleintensive'),
      color: 'from-red-500 to-red-700',
      badge: t('badgeintensive'),
    }
  ];

  const specialPrograms = [
    {
      icon: Target,
      title: t('examPreparation'),
      description: t('examPreparationdescription'),
      color: 'bg-accent-500'
    },
    {
      icon: Users,
      title:  t('familyPackages'),
      description: t('familyPackagesdescription'),
      color: 'bg-primary-500'
    },
    {
      icon: Calendar,
      title: t('summerCamps'),
      description: t('summerCampsdescription'),
      color: 'bg-green-500'
    }
  ];

  return (
    <section id="courses" className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-primary-500/10 text-primary-500 mb-6">
            <Calendar className="w-5 h-5 mr-2" />
            <span className="font-medium">{t('flexibleLearning')}</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold font-heading text-primary-500 mb-6">
            {t('coursesTitle')}
            <span className="block text-accent-500">{t('coursesSubtitle')}</span>
          </h2>
          
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            {t('coursesDescription')}
          </p>
        </motion.div>

        {/* Course Types Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {courseTypes.map((course, index) => (
            <motion.div
              key={course.title}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.2, duration: 0.8 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border border-neutral-100 group"
            >
              {/* Course Image */}
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={course.image} 
                  alt={course.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-r ${course.color} opacity-80`}></div>
                
                {/* Badge */}
                {course.badge && (
                  <div className="absolute top-4 right-4 bg-accent-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {course.badge}
                  </div>
                )}
                
                {/* Icon */}
                <div className="absolute top-4 left-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <course.icon className="w-6 h-6 text-white" />
                </div>
              </div>

              {/* Course Content */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-primary-500 mb-2 group-hover:text-accent-500 transition-colors">
                  {course.title}
                </h3>
                <p className="text-accent-500 font-medium mb-4">{course.subtitle}</p>
                <p className="text-neutral-600 mb-6 leading-relaxed">{course.description}</p>

                {/* Features */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {course.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-accent-500 rounded-full"></div>
                      <span className="text-sm text-neutral-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Course Details */}
                <div className="flex items-center justify-between text-sm text-neutral-600 mb-6">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{course.duration}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{course.schedule}</span>
                  </div>
                </div>

                {/* Price and CTA */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-primary-500">{course.price}</span>
                  </div>
                  <motion.a
                    href="#contact"
                    className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-2xl font-semibold flex items-center space-x-2 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>{t('learnMore')}</span>
                    <ArrowRight className="w-4 h-4" />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Special Programs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl font-bold text-primary-500 mb-12">{t('specialPrograms')}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {specialPrograms.map((program, index) => (
              <motion.div
                key={program.title}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-neutral-100"
              >
                <div className={`w-12 h-12 ${program.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <program.icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-bold text-primary-500 mb-3">{program.title}</h4>
                <p className="text-neutral-600">{program.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-3xl p-8 lg:p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              {t('readyToStart')}
            </h3>
            <p className="text-xl mb-8 opacity-90">
              {t('readyDescription')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                className="bg-white text-primary-500 hover:bg-neutral-50 font-semibold px-8 py-4 rounded-2xl transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = "#contact"}
              >
                  {t('bookConsultation')}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Courses;
